import { Fragment, useEffect, useState, useRef } from "react";
import OnboardCpaSteps from "../../ui/steps/OnboardCpaSteps.js";
import {
  Form,
  redirect,
  useNavigate,
  useSubmit,
  Navigate,
} from "react-router-dom";
import { submitLicense } from "../../../api/OnboardCpa/cpaLicense.js";
import { Handler401 } from "../../../api/utils/responseActions.js";
import { cleanPhoneNumber } from "../../../utils/formatting/phoneFormatter";
import SmallAlert from "../../ui/alerts/SmallAlert";
import LicenseValidationNotificationService from "../../../services/sockets/notifications/LicenseValidationNotificationService";
import ModalNew from "../../ui/popup/Modal";
import { PopupHeader, PopupContent } from "../../ui/popup/common";
import LoadingPlaceholder from "../../../components/ui/loading/LoadingPlaceholder";
import FormSectionWrapper from "../../ui/account/FormSectionWrapper";
import FieldLabel from "../../ui/forms/FieldLabel";
import DateInput from "../../ui/forms/DateInput";
import PhoneInput from "../../ui/forms/PhoneInput";
import SingleAddress from "../../ui/forms/address/SingleAddress";
import { useGetAccountQuery } from "../../../api/account";
import ApiResponseModal from "../../ui/popup/ApiResponseModal";
import AvatarUploadModal from "../../ui/forms/AvatarUploadModal";
import { updateSettingsAvatar } from "../../../api/account/accountAPI";
import { useDispatch, useSelector } from "react-redux";
import { accountApi } from "../../../api/account";
import { updateUserStatus } from "../../../store/redux_auth/authSlice";
import { store } from "../../../store";

function ProvideLicenseV2() {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const cpaStatus = user?.account_status;
  const userRole = user?.user_role;

  const [validationStatus, setValidationStatus] = useState(null);
  const [licenseNotification, setLicenseNotification] = useState(null);
  const [rejectionData, setRejectionData] = useState(null); // New state for rejection
  const [showSuccessPopup, setShowSuccessPopup] = useState(false); // New state for success popup
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const submit = useSubmit();

  // Get account data to populate form fields
  const { data: accountInfo, isLoading: isLoadingAccount } =
    useGetAccountQuery();

  // Avatar state - store locally, upload on form submission
  const [avatarUrl, setAvatarUrl] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null); // Store file to upload on form submission
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isAvatarResultModalOpen, setIsAvatarResultModalOpen] = useState(false);
  const [avatarResultMessage, setAvatarResultMessage] = useState("");
  const [isAvatarSuccess, setIsAvatarSuccess] = useState(false);
  const [isAvatarResultLoading, setIsAvatarResultLoading] = useState(false);

  // Form state for address
  const [formState, setFormState] = useState({
    userAddress: {},
  });

  // Form field values for validation
  const [licenseContent, setLicenseContent] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");

  // Check if all required fields are populated
  const isFormValid = () => {
    const hasLicense = licenseContent.trim().length > 0;
    const hasPhone = phoneNumber.trim().length >= 10; // At least 10 digits
    const hasDob = dateOfBirth.trim().length > 0;
    const hasAddress =
      formState.userAddress?.address?.trim().length > 0 &&
      formState.userAddress?.city?.trim().length > 0 &&
      formState.userAddress?.stateAddress?.trim().length > 0 &&
      formState.userAddress?.zipCode?.trim().length > 0;

    return hasLicense && hasPhone && hasDob && hasAddress;
  };

  // Track if address has been initialized from accountInfo (to prevent resetting user input)
  const addressInitializedRef = useRef(false);

  // Load avatar and form fields from account data
  useEffect(() => {
    if (accountInfo?.personal_info?.avatar) {
      setAvatarUrl(accountInfo.personal_info.avatar);
    }
    if (accountInfo?.personal_info?.phone_number) {
      setPhoneNumber(accountInfo.personal_info.phone_number);
    }
    if (accountInfo?.personal_info?.date_of_birth) {
      setDateOfBirth(accountInfo.personal_info.date_of_birth);
    }
  }, [accountInfo]);

  // Load address from account data and convert to SingleAddress format
  // Only initialize once from accountInfo, don't reset if user has already set an address
  useEffect(() => {
    // Only initialize once, and only if we haven't already set an address from user input
    if (addressInitializedRef.current) return;

    if (
      accountInfo?.addresses &&
      accountInfo.addresses.length > 0 &&
      accountInfo.addresses[0]
    ) {
      const apiAddress = accountInfo.addresses[0];
      const addressData = {
        address: apiAddress.address_line_one || "",
        suiteAddress: apiAddress.address_apt || "",
        city: apiAddress.address_city || "",
        stateAddress: apiAddress.address_state || "",
        zipCode: apiAddress.address_zip || "",
        country: apiAddress.address_country || "US",
      };
      // Only update if we have at least one meaningful field
      if (addressData.address || addressData.city || addressData.stateAddress) {
        // Set the consolidated address object (SingleAddress reads from formState[fieldName])
        setFormState((prev) => ({
          ...prev,
          userAddress: addressData,
        }));
        addressInitializedRef.current = true;
      }
    } else if (accountInfo) {
      // Only set empty if accountInfo has loaded (to avoid resetting user input)
      // SingleAddress will show "Add Address" button
      setFormState((prev) => {
        // Only set empty if address is already empty (don't overwrite user input)
        if (!prev.userAddress || Object.keys(prev.userAddress).length === 0) {
          return { ...prev, userAddress: {} };
        }
        return prev;
      });
      addressInitializedRef.current = true;
    }
  }, [accountInfo]);

  const handleCloseAvatarResultModal = () => {
    setIsAvatarResultModalOpen(false);
    setAvatarResultMessage("");
  };

  // Check if user already has rejection status on component mount
  useEffect(() => {
    // If user has LR status, it means their license was rejected
    if (cpaStatus === "LR") {
      // Show rejection screen with generic message
      setRejectionData({
        comment: "",
        timestamp: Date.now(),
      });
    } else {
      setRejectionData(null);
    }

    // Set loading to false after component mounts
    setLoading(false);
  }, [cpaStatus]);

  // Handle success popup close and navigation
  const handleSuccessPopupClose = () => {
    setShowSuccessPopup(false);
    // Update Redux cpa_status to CA (Connected Account needed)
    dispatch(updateUserStatus({ account_status: "CA" }));
    // Navigate to next step
    navigate("/onboard", { replace: true });
  };

  // Handle license validation status updates - similar to OnboardingLegal
  useEffect(() => {
    if (validationStatus === "approved" || validationStatus === "rejected") {
      // Get the comment from temp storage
      const tempData = sessionStorage.getItem("temp_validation_data");
      let comment = "";

      if (tempData) {
        try {
          const data = JSON.parse(tempData);
          comment = data.comment || "";
        } catch (e) {}
        // Clean up temp data
        sessionStorage.removeItem("temp_validation_data");
      }

      if (validationStatus === "approved") {
        // Clear any rejection data if user was previously rejected
        setRejectionData(null);
        // Show success popup first, update localStorage later when navigating
        setShowSuccessPopup(true);
      } else if (validationStatus === "rejected") {
        // Update Redux cpa_status to LR (License Rejected)
        dispatch(updateUserStatus({ account_status: "LR" }));

        // Set rejection data to show rejection UI instead of navigating
        setRejectionData({
          comment: comment,
          timestamp: Date.now(),
        });
      }
    }
  }, [validationStatus, navigate]);

  // WebSocket listener for real-time license validation updates using the service
  useEffect(() => {
    // Connect if user is in License Validation status OR License Rejected status
    if (cpaStatus === "LV" || cpaStatus === "LR") {
      const socketService = new LicenseValidationNotificationService();
      const subscription = socketService.getMessages().subscribe((message) => {
        if (message.type === "tax_expert_license_validation") {
          // Store the full message data for use in navigation
          const decision =
            message.decision === "Approved" ? "approved" : "rejected";
          const comment = message.comment || "";

          // Store decision and comment for the navigation effect
          sessionStorage.setItem(
            "temp_validation_data",
            JSON.stringify({
              decision: decision,
              comment: comment,
            })
          );

          setValidationStatus(decision);
        }
      });

      return () => subscription.unsubscribe();
    }
  }, [cpaStatus]);

  // Check for license validation notification from previous navigation
  useEffect(() => {
    const notification = sessionStorage.getItem(
      "license_validation_notification"
    );
    if (notification) {
      try {
        const notificationData = JSON.parse(notification);
        // Only show if notification is recent (within last 30 seconds)
        if (Date.now() - notificationData.timestamp < 30000) {
          setLicenseNotification(notificationData);
        }
        // Clear the notification from sessionStorage
        sessionStorage.removeItem("license_validation_notification");
      } catch (e) {}
    }
  }, []);

  // Redirect if user shouldn't be on this step
  // License step is only for status "DN", "LV", or "LR"
  if (cpaStatus && !["DN", "LV", "LR"].includes(cpaStatus)) {
    return <Navigate to="/onboard" replace />;
  }

  return (
    <Fragment>
      <div className="main-content-area">
        <div className="content">
          {loading ? (
            <div className="internal-content-body">
              <LoadingPlaceholder rows={1} type="card-large" />
            </div>
          ) : userRole !== "CPA" ? (
            <Navigate to="/" replace />
          ) : (
            <Fragment>
              <OnboardCpaSteps />

              {/* License submission form - shown only when status is DN and no rejection data */}
              {cpaStatus === "DN" && !rejectionData && (
                <Form
                  method="post"
                  encType="multipart/form-data"
                  onSubmit={async (e) => {
                    // Upload avatar if a new file was selected (before form submission)
                    if (avatarFile) {
                      e.preventDefault();
                      try {
                        const avatarResponse = await updateSettingsAvatar(
                          avatarFile
                        );
                        if (avatarResponse && avatarResponse.avatar) {
                          setAvatarUrl(avatarResponse.avatar);
                          // Invalidate cache
                          dispatch(
                            accountApi.util.invalidateTags(["account-info"])
                          );
                        }
                        // Continue with form submission using React Router's submit
                        const formData = new FormData(e.target);
                        submit(formData, {
                          method: "post",
                          encType: "multipart/form-data",
                        });
                      } catch (error) {
                        console.error("Avatar upload failed:", error);
                        // Still submit the form even if avatar upload fails
                        const formData = new FormData(e.target);
                        submit(formData, {
                          method: "post",
                          encType: "multipart/form-data",
                        });
                      }
                    }
                    // If no avatar file, form submits normally through React Router
                  }}
                >
                  <FormSectionWrapper
                    title="Verify Your Credentials"
                    infoTooltip="Once verified, you'll unlock access to Plaintr's client
                      network and start building your profile. Approval is
                      quick, and you only need to do it once."
                  >
                    {/* Hidden inputs for address data */}
                    <input
                      type="hidden"
                      name="address"
                      value={formState.userAddress?.address || ""}
                    />
                    <input
                      type="hidden"
                      name="suiteAddress"
                      value={formState.userAddress?.suiteAddress || ""}
                    />
                    <input
                      type="hidden"
                      name="city"
                      value={formState.userAddress?.city || ""}
                    />
                    <input
                      type="hidden"
                      name="stateAddress"
                      value={formState.userAddress?.stateAddress || ""}
                    />
                    <input
                      type="hidden"
                      name="zipCode"
                      value={formState.userAddress?.zipCode || ""}
                    />
                    <input
                      type="hidden"
                      name="country"
                      value={formState.userAddress?.country || "US"}
                    />
                    <div className="personal-info-container">
                      {/* First Column */}
                      <div className="personal-info-left">
                        {/* Profile Image Section */}
                        <div className="profile-image-section">
                          <div className="profile-image">
                            <img
                              src={
                                avatarUrl ||
                                `${process.env.PUBLIC_URL}/images/avatar_settings.svg`
                              }
                              alt="Profile Avatar"
                            />
                          </div>
                          <button
                            type="button"
                            className="update-image-link"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setIsAvatarModalOpen(true);
                            }}
                          >
                            <img
                              src="/images/icons/upload_blue.svg"
                              alt="Upload"
                              className="upload-icon"
                            />
                            {avatarUrl ? "Update Image" : "Upload Image"}
                          </button>
                        </div>

                        {/* Address Section */}
                        {!isLoadingAccount && (
                          <SingleAddress
                            formState={formState}
                            setFormState={setFormState}
                            fieldNames={{
                              address: "address",
                              suiteAddress: "suiteAddress",
                              city: "city",
                              stateAddress: "stateAddress",
                              zipCode: "zipCode",
                            }}
                            formMethods={null}
                            fieldName="userAddress"
                            showDelete={false}
                          />
                        )}
                      </div>

                      {/* Second Column */}
                      <div className="personal-info-right">
                        {/* License Type - Display only */}
                        <div className="form-field">
                          <FieldLabel
                            label="License Type"
                            htmlFor="license_type"
                          />
                          <div className="form-input-representation">
                            <p>
                              {accountInfo?.personal_info?.license_type ||
                                "N/A"}
                            </p>
                          </div>
                        </div>

                        {/* License Value - User input */}
                        <div className="form-field">
                          <FieldLabel
                            label="License Number"
                            htmlFor="licenseContent"
                          />
                          <input
                            type="text"
                            id="licenseContent"
                            name="licenseContent"
                            className="form-input"
                            autoComplete="off"
                            required
                            value={licenseContent}
                            onChange={(e) => setLicenseContent(e.target.value)}
                          />
                        </div>

                        {/* Phone Number */}
                        <div className="form-field">
                          <FieldLabel
                            label="Phone Number"
                            htmlFor="phoneNumber"
                          />
                          <PhoneInput
                            id="phoneNumber"
                            name="phoneNumber"
                            value={phoneNumber}
                            onChange={(e) => setPhoneNumber(e.target.value)}
                          />
                        </div>

                        {/* Date of Birth */}
                        <div className="form-field">
                          <FieldLabel
                            label="Date of Birth"
                            htmlFor="dateOfBirth"
                          />
                          <DateInput
                            name="dateOfBirth"
                            id="dateOfBirth"
                            label="Date of Birth"
                            value={dateOfBirth}
                            onChange={(e) => setDateOfBirth(e.target.value)}
                          />
                        </div>

                        <button
                          type="submit"
                          className="cta-button primary"
                          data-icon="arrow_forward"
                          disabled={!isFormValid()}
                        >
                          <b>Get Verified</b>
                        </button>
                      </div>
                    </div>
                  </FormSectionWrapper>
                </Form>
              )}
            </Fragment>
          )}

          {/* License verification status - shown when status is LV and no rejection data */}
          {!loading && cpaStatus === "LV" && !rejectionData && (
            <div className="infoQuestions inProgress">
              <h1>We're verifying your credentials.</h1>
              <p>
                Our team is reviewing your license to confirm everything checks
                out. Once approved, you'll be guided through the remaining
                onboarding steps so you can start accepting clients.
              </p>
            </div>
          )}
          {!loading && cpaStatus === "LR" && rejectionData && (
            <div className="infoQuestions warning">
              <h1>Your License have been rejected</h1>
              <p>
                We were not able to verify it with the issuing authority using
                the information provided.
              </p>
              <p>
                Do not worry — this is usually due to a small mismatch and can
                often be resolved quickly. Please review the details below and
                update your information so we can continue your onboarding.
              </p>
              <ul>
                <li>
                  The name on the license does not exactly match your profile
                </li>
                <li>
                  The address on your profile does not match the address on file
                  with the licensing authority
                </li>
                <li>
                  The license number or state may not match official records
                </li>
              </ul>
              <p>
                Please review your profile and update any information that may
                be incorrect. Once you have made changes, contact our support team
                so we can review your submission again.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Success Popup when license is approved */}
      {showSuccessPopup && (
        <ModalNew className="content-modal" style={{ maxWidth: "480px" }}>
          <PopupHeader
            title="License Validated!"
            setOpenState={(isOpen) => {
              if (!isOpen) handleSuccessPopupClose();
            }}
            icon={`${process.env.PUBLIC_URL}/images/icons/survey_blue_popup.svg`}
            variant="compact"
          />
          <PopupContent>
            <div style={{ textAlign: "center", padding: "20px 0" }}>
              {/* Success message */}
              <p
                style={{
                  color: "#666",
                  marginBottom: "24px",
                  lineHeight: "1.5",
                }}
              >
                Your license has been approved successfully. You can now
                continue with the next step of the registration process.
              </p>

              {/* Action button */}
              <button
                onClick={handleSuccessPopupClose}
                className="cta-button primary"
              >
                Continue to Next Step
              </button>
            </div>
          </PopupContent>
        </ModalNew>
      )}

      {/* Avatar Upload Modal */}
      <AvatarUploadModal
        isOpen={isAvatarModalOpen}
        onClose={() => setIsAvatarModalOpen(false)}
        avatarUrl={avatarUrl}
        uploadFunction={async (file) => {
          // Store the file instead of uploading immediately
          // We'll upload it when the form is submitted
          setAvatarFile(file);
          // Create a preview URL for display
          const previewUrl = URL.createObjectURL(file);
          setAvatarUrl(previewUrl);
          setIsAvatarModalOpen(false);
          return { avatar: previewUrl }; // Return mock response for modal
        }}
        onUploadSuccess={(newAvatarUrl) => {
          // Avatar is stored in state, will be uploaded on form submission
          setIsAvatarModalOpen(false);
        }}
        onUploadError={(errorMessage) => {
          setIsAvatarModalOpen(false);
          setIsAvatarResultModalOpen(true);
          setIsAvatarResultLoading(false);
          setIsAvatarSuccess(false);
          setAvatarResultMessage(errorMessage);
        }}
      />

      {/* Avatar Result Modal */}
      <ApiResponseModal
        isOpen={isAvatarResultModalOpen}
        isLoading={isAvatarResultLoading}
        isSuccess={isAvatarSuccess}
        message={avatarResultMessage}
        onClose={handleCloseAvatarResultModal}
      />
    </Fragment>
  );
}

export default ProvideLicenseV2;

export async function action({ request }) {
  const url = "utils/validate-license";
  const data = await request.formData();

  // Collect address data if present and transform to API format
  const addressData =
    data.get("address") || data.get("city") || data.get("stateAddress")
      ? {
          address_line_one: data.get("address") || "",
          address_line_two: "",
          address_apt: data.get("suiteAddress") || "",
          address_city: data.get("city") || "",
          address_state: data.get("stateAddress") || "",
          address_zip: data.get("zipCode") || "",
          address_country: data.get("country") || "US",
        }
      : null;

  const rawPhoneNumber = data.get("phoneNumber");
  const licenseData = {
    licenseContent: data.get("licenseContent"),
    phoneNumber: rawPhoneNumber ? cleanPhoneNumber(rawPhoneNumber) : null,
    dateOfBirth: data.get("dateOfBirth") || null,
    ...(addressData && { address: addressData }),
  };

  const response = await submitLicense(url, licenseData);

  if (response.ok) {
    // Update Redux cpa_status to LV (License Validation pending)
    store.dispatch(updateUserStatus({ account_status: "LV" }));
    return redirect("/onboard");
  }
  if (response.status === 401) {
    return Handler401();
  }
}
